import React, { useContext, useMemo } from 'react';
import { Grid, alpha } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, ptBR } from '@mui/x-data-grid';
import './table-info.css';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import { Typography } from '@material-ui/core';

const TableInfo = ({
    rows,
    columns,
    autoHeight,
    id,
    isBorder,
    color,
    height,
    fullHeight,
    onCellEditStart,
    onCellEditStop,
    sortModel,
    initialState,
    onSortModelChange,
    filterModel,
    headerHeight,
    columnHeader,
    CustomToolbar,
    processRowUpdate,
    onProcessRowUpdateError,
    onRowClick,
    onCellClick,
    borderRadius,
    CustomFooter,
    marginBottom,
    isChatList,
    getRowHeight,
    checkboxSelection,
    selectionModel,
    onSelectionModelChange,
    /** 'compact' | 'standard' — listagens densas (ex.: tickets) */
    density = 'standard',
    /** Mescla estilos extra no DataGrid (ex.: tipografia compacta) */
    sx: sxProp,
    /** Linhas zebradas e cabeçalho alinhados ao tema claro/escuro (evita azul legado) */
    useThemeRows = false,
    /** Mostra o rodapé com paginação (pager N/página). O DataGrid community força
     *  paginação e limita a 100/página; use isto para listas grandes. */
    showPagination = false,
    pageSize,
    onPageChange,
    rowsPerPageOptions,
}) => {

    const { theme } = useContext(ThemeContext);

    const themeRowStyles = useMemo(() => {
        if (!useThemeRows) return null;
        const isDark = theme.palette.mode === 'dark';
        return {
            even: isDark
                ? alpha(theme.palette.common.white, 0.03)
                : theme.palette.background.paper,
            odd: isDark
                ? alpha(theme.palette.common.white, 0.06)
                : alpha(theme.palette.primary.main, 0.04),
            header: isDark
                ? alpha(theme.palette.common.white, 0.08)
                : theme.palette.background.secondary,
        };
    }, [useThemeRows, theme]);
    const CustomNoRowsOverlay = () => {
        return (
            <Grid style={{ textAlign: 'center', padding: '10px', color: theme.palette.text.secondary }}>
                <span>Nenhum dado encontrado</span>
            </Grid>
        );
    };

    const rowUnit = density === 'compact' ? 40 : 48;

    const evenRowSx = useThemeRows && themeRowStyles
        ? {
            backgroundColor: themeRowStyles.even,
            color: theme.palette.text.primary,
            boxShadow: 'none',
            mixBlendMode: 'unset',
        }
        : color
            ? {
                backgroundColor: color,
                color: theme.palette.text.primary,
            }
            : {
                backgroundColor: '#5799CC',
                color: color === '#fff' ? '#000' : '#fff',
            };

    const oddRowSx = useThemeRows && themeRowStyles
        ? {
            backgroundColor: themeRowStyles.odd,
            color: theme.palette.text.primary,
            boxShadow: 'none',
            mixBlendMode: 'unset',
        }
        : color
            ? {
                backgroundColor: color,
                boxShadow: isChatList ? '' : 'inset 0 0 0 1000px rgba(0, 0, 0, 0.1)',
                mixBlendMode: isChatList ? '' : 'multiply',
                color: theme.palette.text.primary,
            }
            : {
                backgroundColor: '#4B86B4',
                color: color === '#fff' ? '#000' : '#fff',
            };

    const themeGridBaseSx = useThemeRows && themeRowStyles
        ? {
            border: 'none',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '& .MuiDataGrid-columnHeaders': {
                backgroundColor: themeRowStyles.header,
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRadius: 0,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.75rem',
            },
            '& .MuiDataGrid-columnSeparator': {
                color: theme.palette.divider,
            },
            '& .MuiDataGrid-cell': {
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-row:hover': {
                backgroundColor: `${theme.palette.action.hover} !important`,
            },
            '& .MuiDataGrid-row.Mui-hovered': {
                backgroundColor: theme.palette.action.hover,
            },
        }
        : {};

    return (
        /*
         * `xs={12}` num `container` é prop de ITEM, e o MUI avisa no console
         * em TODA tela que tem lista -- que é quase toda tela do sistema.
         *
         * Tirar a prop sem mais nada MUDARIA O LAYOUT: o MUI não ignora o
         * `xs` de um container, ele gera `flexBasis/flexGrow/maxWidth` do
         * mesmo jeito, e este Grid é filho de um flex. Sem a base de 100% ele
         * encolheria até o conteúdo. Por isso os três valores estão escritos
         * aqui: é exatamente o que o `xs={12}` emitia (medido na tela, 838px
         * antes e depois), só sem o aviso.
         */
        <Grid
            container
            sx={{
                flexBasis: '100%',
                flexGrow: 0,
                maxWidth: '100%',
                height: fullHeight ? '90vh' : null,
            }}
            className="table-info"
        >
            <DataGrid
                columns={columns}
                rows={rows}
                density={density}
                autoHeight={autoHeight === false ? autoHeight : true}
                auto
                disableColumnMenu
                disableColumnSelector
                disableSelectionOnClick
                getRowId={id ? (row) => row.id : null}
                {...(showPagination
                    ? { pageSize: pageSize || 100, rowsPerPageOptions: rowsPerPageOptions || [100], onPageChange }
                    : { hideFooterPagination: true })}
                localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                experimentalFeatures={{ newEditingApi: true }}
                getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
                }
                components={{
                    Toolbar: CustomToolbar ? CustomToolbar : GridToolbarContainer,
                    ToolbarExport: GridToolbarExport,
                    ToolbarFilterButton: GridToolbarFilterButton,
                    // Sem CustomFooter: esconde o rodapé (Footer vazio), a não ser que
                    // showPagination esteja ligado — aí usa o rodapé padrão (com o pager).
                    ...(CustomFooter
                        ? { Footer: CustomFooter }
                        : (showPagination ? {} : { Footer: () => <></> })),
                    NoRowsOverlay: CustomNoRowsOverlay,
                }}
                sx={{
                    minHeight: autoHeight === false ? `calc(${height ? height : 5} * ${rowUnit}px)` : null,
                    '& .MuiDataGrid-virtualScroller': {
                        overflowX: density === 'compact' ? 'auto' : 'hidden',
                    },
                    '& .MuiDataGrid-root': isBorder ? null : {
                        border: 'none',
                    },
                    '& .MuiDataGrid-columnSeparator': isBorder || useThemeRows ? null : {
                        display: 'none',
                    },
                    '& .MuiDataGrid-cell': isBorder || useThemeRows ? {
                        cursor: onRowClick ? 'pointer' : 'default',
                    } : {
                        border: 'none',
                        cursor: onRowClick ? 'pointer' : 'default',
                    },
                    '& .MuiDataGrid-row': onRowClick ? {
                        cursor: 'pointer',
                    } : {},
                    '.even-row': evenRowSx,
                    '.odd-row': oddRowSx,
                    backgroundColor: useThemeRows
                        ? theme.palette.background.paper
                        : (color ? color : null),
                    borderRadius: borderRadius ? borderRadius : null,
                    '& .MuiDataGrid-columnHeader': {
                        display: columnHeader ? columnHeader : 'auto',
                    },
                    '& .MuiDataGrid-main': marginBottom ? {
                        marginBottom: marginBottom
                    } : null,
                    ...themeGridBaseSx,
                    ...(typeof sxProp === 'object' && sxProp !== null ? sxProp : {}),
                }}
                onCellEditStop={onCellEditStop ? onCellEditStop : null}
                onCellEditStart={onCellEditStart ? onCellEditStart : null}
                sortModel={sortModel}
                initialState={{ initialState }}
                onSortModelChange={onSortModelChange}
                filterModel={filterModel}
                headerHeight={headerHeight ? headerHeight : 48}
                processRowUpdate={processRowUpdate ? processRowUpdate : null}
                onProcessRowUpdateError={onProcessRowUpdateError ? onProcessRowUpdateError : null}
                onRowClick={onRowClick ? onRowClick : null}
                onCellClick={onCellClick ? onCellClick : null}
                getRowHeight={getRowHeight}
                checkboxSelection={checkboxSelection}
                selectionModel={selectionModel}
                onSelectionModelChange={onSelectionModelChange}
            />
        </Grid>
    );
};

export default TableInfo;
